import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Icon from './icons';

const NTF_KIND = {
    team_invite: { ic: Icon.people, cls: 'indigo', verb: 'invited you' },
    team_added: { ic: Icon.check, cls: 'green', verb: 'added you to' },
    team_removed: { ic: Icon.warn, cls: 'red', verb: 'removed you from' },
    project_assigned: { ic: Icon.flag, cls: 'yellow', verb: 'assigned you' },
    autotranslate_complete: { ic: Icon.bolt, cls: 'accent', verb: 'finished translating' },
    default: { ic: Icon.bell, cls: 'indigo', verb: '' },
};

const relTime = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const m = Math.round(diff / 60000);
    if (m < 1) return 'now';
    if (m < 60) return `${m}m`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h}h`;
    const d = Math.round(h / 24);
    return `${d}d`;
};

const NotificationsMenu = () => {
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const ref = useRef(null);
    const token = useSelector((s) => s.auth.token);
    const navigate = useNavigate();

    useEffect(() => {
        if (!token) return;
        axios
            .get('/api/notifications', { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
            .catch(() => setItems([]));
    }, [token]);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    const unread = items.filter((n) => !n.read).length;

    const markAll = async () => {
        try {
            await axios.patch(
                '/api/notifications/read-all',
                {},
                { headers: { Authorization: `Bearer ${token}` } },
            );
            setItems((arr) => arr.map((n) => ({ ...n, read: true })));
        } catch {
            setItems((arr) => arr.map((n) => ({ ...n, read: true })));
        }
    };

    const grouped = items.reduce(
        (acc, n) => {
            const age = Date.now() - new Date(n.createdAt || Date.now()).getTime();
            const bucket = age < 24 * 3600 * 1000 ? 'Today' : 'Earlier';
            acc[bucket].push(n);
            return acc;
        },
        { Today: [], Earlier: [] },
    );

    return (
        <div className="rs-ntf" ref={ref}>
            <button type="button" className="icon-btn" aria-label="Notifications" onClick={() => setOpen((o) => !o)}>
                {Icon.bell}
                {unread > 0 && <span className="dot-n" />}
            </button>
            {open && (
                <div className="ntf-pop" role="menu">
                    <div className="ntf-head">
                        <b>Notifications</b>
                        {unread > 0 && <span className="ntf-count">{unread} new</span>}
                        <div className="grow" />
                        <button type="button" className="ntf-mark" onClick={markAll} disabled={unread === 0}>
                            Mark all read
                        </button>
                    </div>
                    <div className="ntf-list">
                        {items.length === 0 && (
                            <div className="ntf-day" style={{ padding: '24px 16px', color: 'var(--muted)' }}>
                                You're all caught up.
                            </div>
                        )}
                        {['Today', 'Earlier'].map((day) => {
                            const arr = grouped[day];
                            if (!arr.length) return null;
                            return (
                                <React.Fragment key={day}>
                                    <div className="ntf-day">{day}</div>
                                    {arr.map((n) => {
                                        const k = NTF_KIND[n.type] || NTF_KIND.default;
                                        const un = !n.read;
                                        return (
                                            <button
                                                key={n._id || n.id}
                                                type="button"
                                                className={`ntf-item ${un ? 'unread' : ''}`}
                                                onClick={() => {
                                                    setOpen(false);
                                                    if (n.link) navigate(n.link);
                                                }}
                                            >
                                                <span className={`ntf-ic ${k.cls}`}>{k.ic}</span>
                                                <div className="ntf-body">
                                                    <div className="t">{n.message || `${k.verb} ${n.target || ''}`}</div>
                                                    <div className="when">{relTime(n.createdAt)} ago</div>
                                                </div>
                                                {un && <span className="ntf-udot" />}
                                            </button>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        })}
                    </div>
                    <button type="button" className="ntf-foot" onClick={() => { setOpen(false); navigate('/dashboard/inbox'); }}>
                        View all in inbox {Icon.arrow}
                    </button>
                </div>
            )}
        </div>
    );
};

export default NotificationsMenu;
